import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound, BarChart3, CheckCircle, XCircle, Eye, EyeOff,
  ExternalLink, RefreshCw, AlertTriangle, Trash2, Pencil,
  ShieldAlert, Clock, X, Save, ChevronDown, ChevronUp
} from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '../../contexts/ToastContext';

// ─── Owner gate + page wrapper ────────────────────────────────────────────────
export default function SettingsPage() {
  const navigate   = useNavigate();
  const [user, setUser]           = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeTab, setActiveTab] = useState('integrations');

  useEffect(() => {
    api.get('/auth/me')
      .then(({ data }) => {
        setUser(data);
        setAuthChecked(true);
        const role = data?.workspace_role_raw || data?.workspace_role || '';
        if (!['owner', 'org_admin'].includes(role)) {
          navigate('/jobs', { replace: true });
        }
      })
      .catch(() => { setAuthChecked(true); navigate('/auth', { replace: true }); });
  }, [navigate]); // navigate is stable from useNavigate()

  if (!authChecked) return (
    <div className="flex items-center justify-center h-full p-20">
      <RefreshCw className="w-5 h-5 text-slate-300 animate-spin" />
    </div>
  );

  const role = user?.workspace_role_raw || user?.workspace_role || '';
  if (!user || !['owner', 'org_admin'].includes(role)) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-20 text-center">
        <ShieldAlert className="w-10 h-10 text-red-400 mb-3" />
        <h2 className="text-base font-semibold text-slate-800">Access Denied</h2>
        <p className="text-sm text-slate-500 mt-1">Only workspace owners can access Settings.</p>
        <button onClick={() => navigate('/jobs', { replace: true })}
          className="mt-4 px-4 py-2 bg-[#2563eb] text-white text-sm rounded-xl hover:bg-[#1d4ed8]">
          Back to Jobs
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8 relative" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="max-w-4xl mx-auto">
      <div className="bg-white/90 backdrop-blur-xl border border-slate-200/60 rounded-3xl shadow-xl p-6 mb-8">
        <h1 className="text-3xl font-black bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
          Manage API keys and view usage costs
          <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
            <ShieldAlert className="w-3 h-3" /> Owner only
          </span>
        </p>
      </div>

      <div className="flex border-b border-slate-200 mb-6">
        {[{ id: 'integrations', label: 'Integrations', Icon: KeyRound },
          { id: 'usage',        label: 'Usage & Costs', Icon: BarChart3 }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id ? 'border-[#2563eb] text-[#2563eb]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            <tab.Icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'integrations' && <IntegrationsTab />}
      {activeTab === 'usage'        && <UsageDashboard />}
      </div>
    </div>
  );
}

// ─── Delete confirmation modal ─────────────────────────────────────────────────
function DeleteConfirmModal({ keyLabel, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-800">Delete API Key</h3>
            <p className="text-xs text-slate-400">This cannot be undone</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
          <p className="text-sm text-red-700">
            Permanently delete <strong>{keyLabel}</strong>.
          </p>
          <p className="text-xs text-red-600 mt-1">
            ⚠️ Features using this key will stop immediately.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} disabled={deleting}
            className="flex-1 px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={deleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 disabled:opacity-50">
            {deleting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {deleting ? 'Deleting...' : 'Yes, Delete Key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Single key card ───────────────────────────────────────────────────────────
const TESTABLE = { openai_api_key: 'openai', gemini_api_key: 'gemini', anthropic_api_key: 'anthropic', exa_api_key: 'exa' };

function KeyCard({ integration, onSaved, onDeleted }) {
  const { key_name, label, description, url, prefix, is_set, masked_value, required } = integration;
  const { addToast } = useToast();

  const [mode, setMode]                 = useState('view');
  const [inputValue, setInputValue]     = useState('');
  const [showValue, setShowValue]       = useState(false);
  const [saving, setSaving]             = useState(false);
  const [testing, setTesting]           = useState(false);
  const [testResult, setTestResult]     = useState(null);
  const [saveError, setSaveError]       = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting]         = useState(false);

  const handleSave = async () => {
    if (!inputValue.trim()) { setSaveError('Key cannot be empty.'); return; }
    setSaving(true); setSaveError(''); setTestResult(null);
    try {
      const { data } = await api.post('/settings/integrations', { [key_name]: inputValue.trim() });
      if (data.success) { setMode('view'); setInputValue(''); onSaved(); }
      else setSaveError('Save failed.');
    } catch (err) { setSaveError(err.response?.data?.detail?.message || err.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/settings/integrations/${key_name}`);
      if (data.success) { setShowDeleteModal(false); onDeleted(); }
      else addToast('Delete failed', 'error');
    } catch (err) { addToast(err.response?.data?.detail || 'Delete failed', 'error'); }
    finally { setDeleting(false); }
  };

  const handleTest = async () => {
    const service = TESTABLE[key_name];
    if (!service) return;
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/settings/integrations/test', { service });
      setTestResult(data);
    } catch { setTestResult({ success: false, message: 'Network error' }); }
    finally { setTesting(false); }
  };

  return (
    <>
      {showDeleteModal && <DeleteConfirmModal keyLabel={label} onConfirm={handleDelete} onCancel={() => setShowDeleteModal(false)} deleting={deleting} />}

      <div className={`bg-white/80 backdrop-blur-sm border rounded-2xl p-5 shadow-lg transition-all ${mode === 'edit' ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-slate-200/50'}`}
           data-testid={`key-card-${key_name}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-800">{label}</span>
              {required && <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-medium">Required</span>}
              {is_set
                ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-medium ring-1 ring-emerald-200"><CheckCircle className="w-3 h-3" /> Configured</span>
                : <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium ring-1 ring-amber-200"><AlertTriangle className="w-3 h-3" /> Not set</span>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
          </div>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-[#2563eb] hover:text-[#1d4ed8] ml-3 flex-shrink-0">
            Get key <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {mode === 'view' && is_set && (
          <div className="flex items-center gap-2 mt-3">
            <code className="flex-1 text-xs font-mono bg-slate-100 text-slate-600 px-3 py-2 rounded-lg">{masked_value}</code>
            {TESTABLE[key_name] && (
              <button onClick={handleTest} disabled={testing}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${testing ? 'animate-spin' : ''}`} />
                {testing ? 'Testing...' : 'Test'}
              </button>
            )}
            <button onClick={() => { setMode('edit'); setTestResult(null); }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100"
              data-testid={`update-key-${key_name}`}>
              <Pencil className="w-3.5 h-3.5" /> Update
            </button>
            <button onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
              data-testid={`delete-key-${key_name}`}>
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        )}

        {mode === 'view' && !is_set && (
          <button onClick={() => setMode('edit')}
            className="mt-3 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100"
            data-testid={`add-key-${key_name}`}>
            <Pencil className="w-3.5 h-3.5" /> Add Key
          </button>
        )}

        {mode === 'edit' && (
          <div className="mt-3 space-y-2">
            <div className="relative">
              <input type={showValue ? 'text' : 'password'}
                placeholder={is_set ? 'Paste new key to replace existing...' : `${prefix || 'Paste your API key here'}...`}
                value={inputValue} onChange={e => { setInputValue(e.target.value); setSaveError(''); }}
                autoFocus
                className="w-full border border-slate-200/70 rounded-xl px-4 py-3.5 text-sm pr-10 font-mono bg-slate-50/50 hover:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all shadow-sm"
                data-testid={`key-input-${key_name}`} />
              <button type="button" onClick={() => setShowValue(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {saveError && <p className="text-xs text-red-500 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> {saveError}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setMode('view'); setInputValue(''); setSaveError(''); }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button onClick={handleSave} disabled={saving || !inputValue.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-slate-900 rounded-xl hover:bg-slate-800 disabled:opacity-50 active:scale-95 shadow-md transition-all"
                data-testid={`save-key-${key_name}`}>
                {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? 'Saving...' : is_set ? 'Update Key' : 'Save Key'}
              </button>
            </div>
            {is_set && <p className="text-xs text-slate-400">💡 Replaces current key (<code className="font-mono">{masked_value}</code>) immediately.</p>}
          </div>
        )}

        {testResult && mode === 'view' && (
          <p className={`mt-2 text-xs flex items-center gap-1.5 ${testResult.success ? 'text-emerald-600' : 'text-red-500'}`}>
            {testResult.success ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {testResult.message}
          </p>
        )}
      </div>
    </>
  );
}

// ─── Audit log ────────────────────────────────────────────────────────────────
const KEY_LABELS = { openai_api_key:'OpenAI', gemini_api_key:'Gemini', anthropic_api_key:'Anthropic', exa_api_key:'Exa', signalhire_api_key:'SignalHire', emailit_smtp_key:'EmailIt', telnyx_api_key:'Telnyx', deepgram_api_key:'Deepgram', minimax_api_key:'MiniMax TTS' };
const ACTION_COLORS = { deleted:'text-red-600 bg-red-50', updated:'text-indigo-600 bg-indigo-50', added:'text-emerald-600 bg-emerald-50' };

function AuditLog() {
  const [open, setOpen] = useState(false);
  const [log, setLog]   = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLog = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/integrations/audit-log');
      setLog(data.audit_log || []);
    } catch (err) { logger.error('[AuditLog] load failed:', err?.message); }
    setLoading(false);
  };

  const toggle = () => { setOpen(p => !p); if (!open && log.length === 0) loadLog(); };

  return (
    <div className="mt-5 border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
        <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Key Change History</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
          {loading ? <p className="text-xs text-slate-400">Loading...</p>
          : log.length === 0 ? <p className="text-xs text-slate-400">No key changes recorded yet.</p>
          : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {log.map((e, i) => {
                const action = e.action || 'updated';
                const color  = ACTION_COLORS[action] || ACTION_COLORS.updated;
                const keys   = (e.keys_updated || []).map(k => KEY_LABELS[k] || k).join(', ');
                const time   = e.timestamp ? new Date(e.timestamp).toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : '';
                return (
                  <div key={e.timestamp || e.changed_by_name || i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded font-semibold capitalize ${color}`}>{action}</span>
                      <span className="text-slate-700 font-medium">{keys}</span>
                      <span className="text-slate-400">by {e.changed_by_name || 'Owner'}</span>
                    </div>
                    <span className="text-slate-400 ml-3">{time}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Integrations tab ─────────────────────────────────────────────────────────
function IntegrationsTab() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [lastUpdated, setLastUpdated]   = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/settings/integrations');
      setIntegrations(data.integrations || []);
      setLastUpdated(data.updated_at);
    } catch (err) { logger.error('[IntegrationsTab] load failed:', err?.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const required = integrations.filter(i => i.required);
  const optional = integrations.filter(i => !i.required);
  const allRequiredSet = required.every(i => i.is_set);

  if (loading) return (
    <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
  );

  return (
    <div>
      {!allRequiredSet ? (
        <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">{required.filter(i => !i.is_set).length} required key{required.filter(i => !i.is_set).length > 1 ? 's' : ''} missing</p>
            <p className="text-xs text-amber-700 mt-0.5">AI scoring and sourcing will not work until all required keys are configured.</p>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5">
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">All required keys configured ✓</p>
            <p className="text-xs text-emerald-700 mt-0.5">Vocala AI pipeline is fully operational.{lastUpdated && ` Last updated: ${new Date(lastUpdated).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}`}</p>
          </div>
        </div>
      )}
      <div className="flex gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-xs text-slate-500">
        <ShieldAlert className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <span>Keys are <strong className="text-slate-700">AES-256 encrypted</strong> before storage. Masked values only are ever displayed. Full keys are never retrievable — paste a new key to update.</span>
      </div>

      <div className="mb-5">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Required — AI Scoring Pipeline</h2>
        <div className="space-y-3">{required.map(i => <KeyCard key={i.key_name} integration={i} onSaved={load} onDeleted={load} />)}</div>
      </div>
      <div className="mb-5">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Optional — Enrichment & Communication</h2>
        <div className="space-y-3">{optional.map(i => <KeyCard key={i.key_name} integration={i} onSaved={load} onDeleted={load} />)}</div>
      </div>
      {/* Voice AI section */}
      <VoiceKeysSection onRefresh={load} />
      <AuditLog />
    </div>
  );
}

// ─── Voice AI Keys Section ────────────────────────────────────────────────────
function VoiceKeysSection({ onRefresh }) {
  const [status,      setStatus]     = useState({ telnyx_configured: false, minimax_configured: false });
  const [telnyxKey,   setTelnyxKey]  = useState('');
  const [minimaxKey,  setMinimaxKey] = useState('');
  const [saving,      setSaving]     = useState(false);
  const [msg,         setMsg]        = useState(null); // {text, type}

  useEffect(() => {
    api.get('/settings/voice-keys').then(r => setStatus(r.data)).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const { data } = await api.post('/settings/voice-keys', {
        telnyx_api_key:  telnyxKey  || null,
        minimax_api_key: minimaxKey || null,
      });
      setStatus(data);
      setTelnyxKey(''); setMinimaxKey('');
      setMsg({ text: 'Voice API keys saved.', type: 'success' });
      if (onRefresh) onRefresh();
    } catch (err) {
      logger.error('[VoiceSettings] save failed:', err?.message);
      setMsg({ text: 'Unable to save keys. Check your role and try again.', type: 'error' });
    } finally { setSaving(false); }
  };

  return (
    <div className="mb-5">
      <h2 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Voice AI — Telephony & TTS</h2>
      <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-5 shadow-sm">
        <p className="text-xs text-slate-500 mb-4">
          Configure Telnyx (call control + media streaming) and MiniMax (TTS voice synthesis).
          Keys are encrypted and never displayed in full.
        </p>
        {msg && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium mb-3 ${
            msg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {msg.type === 'success' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {msg.text}
          </div>
        )}
        <form onSubmit={handleSave} className="space-y-4">
          {[
            { label: 'Telnyx API Key',   val: telnyxKey,  set: setTelnyxKey,  cfg: status.telnyx_configured,  ph: 'KEY_xxxxxxxx' },
            { label: 'MiniMax API Key',  val: minimaxKey, set: setMinimaxKey, cfg: status.minimax_configured, ph: 'Enter MiniMax API key' },
          ].map(({ label, val, set, cfg, ph }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
                <input
                  type="password"
                  value={val}
                  onChange={e => set(e.target.value)}
                  placeholder={cfg ? '•••••••• (configured)' : ph}
                  className="w-full px-3 py-2 border border-slate-200/70 rounded-xl bg-slate-50/50 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all hover:bg-white"
                />
              </div>
              <span className={`flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                cfg ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
                {cfg ? 'Configured' : 'Not set'}
              </span>
            </div>
          ))}
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-sm hover:shadow-md hover:scale-[1.01] active:scale-[0.98] transition-all disabled:opacity-50">
            {saving ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving...</> : 'Save voice keys'}
          </button>
        </form>
      </div>
    </div>
  );
}


// ─── Usage Dashboard ──────────────────────────────────────────────────────────
const SERVICE_LABELS = {
  linkedin_scorer:'LinkedIn Scoring (o4-mini)', linkedin_gate:'LI Gate (Gemini Flash)',
  resume_scorer:'Resume Scoring (o4-mini)', transcript_scorer:'Transcript Scoring (o4-mini)',
  screening_questions:'Screening Questions (o4-mini)', weight_generator:'JD Weight Gen (Gemini Pro)',
  jd_query_generator:'JD Query Gen (Claude)', sourcing_agent:'Candidate Sourcing (Exa)',
};

function UsageDashboard() {
  const [view, setView]   = useState('summary');
  const [days, setDays]   = useState(30);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/settings/usage/${view}?days=${days}`)
      .then(({ data: d }) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [view, days]);

  const VIEWS = [{ id:'summary', l:'Overview' }, { id:'by-job', l:'By Job' }, { id:'by-activity', l:'By Activity' }, { id:'by-member', l:'By Member' }];

  // ─── Sparkline SVG ─────────────────────────────────────────────────────────
  const Sparkline = ({ points, width = 280, height = 40, color = '#6366f1' }) => {
    if (!points || points.length < 2) return <div className="h-10 flex items-center justify-center text-[10px] text-slate-300">No data</div>;
    const maxV = Math.max(...points.map(p => p.cost), 0.0001);
    const W = width, H = height, pad = 3;
    const xs = points.map((_, i) => pad + (i / (points.length - 1)) * (W - pad * 2));
    const ys = points.map(p => H - pad - ((p.cost / maxV) * (H - pad * 2)));
    const d  = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
    const fill = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ')
      + ` L${xs[xs.length-1].toFixed(1)},${H} L${xs[0].toFixed(1)},${H} Z`;
    return (
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
        <path d={fill} fill={color} fillOpacity="0.08" />
        <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={p.date || i} cx={xs[i]} cy={ys[i]} r="2" fill={color} opacity="0.7">
            <title>{p.date}: ${p.cost.toFixed(4)} ({p.calls} calls)</title>
          </circle>
        ))}
      </svg>
    );
  };

  // ─── Donut chart (CSS conic-gradient) ──────────────────────────────────────
  const DonutChart = ({ segments }) => {
    if (!segments || segments.length === 0) return null;
    const total = segments.reduce((s, x) => s + x.value, 0);
    if (total === 0) return null;
    const COLORS = ['#6366f1','#06b6d4','#10b981','#f59e0b','#ef4444','#8b5cf6'];
    let angle = 0;
    const stops = segments.map((seg, i) => {
      const pct = (seg.value / total) * 100;
      const stop = `${COLORS[i % COLORS.length]} ${angle.toFixed(1)}% ${(angle + pct).toFixed(1)}%`;
      angle += pct;
      return stop;
    });
    return (
      <div className="flex items-center gap-4">
        <div style={{
          width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
          background: `conic-gradient(${stops.join(', ')})`,
        }} />
        <div className="space-y-1">
          {segments.map((seg, i) => (
            <div key={seg.label || i} className="flex items-center gap-2 text-[10px]">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="text-slate-600 truncate max-w-[120px]">{seg.label}</span>
              <span className="text-slate-400 ml-auto">{((seg.value / total) * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!data) return null;
    const cc = data.pct_change || 0;
    const exact = (data.token_source_split || []).find(s => s.source === 'exact');
    const exactPct = exact ? Math.round(exact.calls / Math.max(data.total_calls, 1) * 100) : 0;

    return (
      <div>
        {/* KPI cards */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          {[
            { l:'Total Spend', v:`~$${(data.total_cost_usd||0).toFixed(2)}`, s:`${cc>0?'↑':'↓'} ${Math.abs(cc)}% vs prev`, sc: cc>0?'text-red-500':'text-emerald-600' },
            { l:'API Calls', v:(data.total_calls||0).toLocaleString(), s:`Last ${days} days`, sc:'text-slate-400' },
            { l:'Avg Cost/Call', v:`~$${data.total_calls>0?(data.total_cost_usd/data.total_calls).toFixed(4):'0.00'}`, s: exactPct > 0 ? `${exactPct}% exact tokens` : 'estimated tokens', sc:'text-slate-400' },
          ].map(c => (
            <div key={c.l} className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-5 shadow-lg">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">{c.l}</p>
              <p className="text-3xl font-black text-slate-900 mt-1 mb-1">{c.v}</p>
              <p className={`text-xs mt-1 ${c.sc}`}>{c.s}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* Daily spend sparkline */}
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-5 shadow-lg">
            <h3 className="text-xs font-bold text-slate-700 mb-3">Daily Spend Trend</h3>
            {(data.daily_spend || []).length > 0
              ? <Sparkline points={data.daily_spend} width={300} height={48} />
              : <p className="text-xs text-slate-400 py-4 text-center">No daily data yet</p>}
          </div>

          {/* Provider donut */}
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-5 shadow-lg">
            <h3 className="text-xs font-bold text-slate-700 mb-3">Cost by Service</h3>
            <DonutChart segments={(data.by_service||[]).slice(0,5).map(s => ({
              label: SERVICE_LABELS[s.service] || s.service, value: s.cost
            }))} />
          </div>
        </div>

        {/* Service breakdown bars */}
        <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-5 shadow-lg">
          <h3 className="text-xs font-bold text-slate-700 mb-3">Breakdown</h3>
          <div className="space-y-2.5">
            {(data.by_service||[]).map(s => {
              const max = Math.max(...(data.by_service||[]).map(x => x.cost), 0.0001);
              return (
                <div key={s.service} className="flex items-center gap-3">
                  <p className="text-xs text-slate-600 w-52 truncate">{SERVICE_LABELS[s.service] || s.service}</p>
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                    <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width:`${(s.cost/max)*100}%` }} />
                  </div>
                  <p className="text-xs font-semibold text-slate-700 w-16 text-right">~${s.cost.toFixed(4)}</p>
                  <p className="text-xs text-slate-400 w-12 text-right">{s.calls}</p>
                </div>
              );
            })}
            {(!data.by_service||data.by_service.length===0) && <p className="text-sm text-slate-400 py-4 text-center">No usage data yet.</p>}
          </div>
        </div>
      </div>
    );
  };

  const Table = ({ rows, cols }) => {
    const [sortKey, setSortKey] = useState('');
    const [sortAsc, setSortAsc] = useState(false);
    const handleSort = k => { if (sortKey === k) setSortAsc(a => !a); else { setSortKey(k); setSortAsc(false); } };
    const sorted = [...(rows||[])].sort((a, b) => {
      if (!sortKey) return 0;
      const va = a[sortKey]??0, vb = b[sortKey]??0;
      if (typeof va === 'string') return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortAsc ? va - vb : vb - va;
    });
    return (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>{cols.map(c => (
              <th key={c.k}
                onClick={() => c.sort !== false && handleSort(c.k)}
                className={`px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${c.sort!==false?'cursor-pointer hover:text-slate-800 select-none':''}`}>
                {c.l}{sortKey===c.k?(sortAsc?' ↑':' ↓'):''}
              </th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {sorted.length === 0
              ? <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-sm text-slate-400">No data for this period.</td></tr>
              : sorted.map((r, i) => <tr key={r.user_id || r.candidate_id || r.id || i} className="hover:bg-slate-50">{cols.map(c => <td key={c.k} className="px-4 py-3 text-xs text-slate-700">{c.r ? c.r(r) : r[c.k]}</td>)}</tr>)
            }
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view===v.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {v.l}
            </button>
          ))}
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none">
          {[7, 30, 90].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      {loading ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div> : (
        <>
          {view === 'summary'     && renderSummary()}
          {view === 'by-job'      && <Table rows={data?.by_job||[]} cols={[
            { k:'job_title', l:'Job Role',   r: r => <span className="font-medium text-slate-800">{r.job_title||'Unknown'}</span> },
            { k:'client',    l:'Client',     r: r => r.client||'—' },
            { k:'job_type',  l:'Type',       r: r => r.job_type ? <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.job_type==='W2'?'bg-blue-100 text-blue-700':'bg-orange-100 text-orange-700'}`}>{r.job_type}</span> : '—' },
            { k:'sourcing_cost',  l:'Sourcing',  r: r => `~$${r.sourcing_cost.toFixed(3)}` },
            { k:'scoring_cost',   l:'Scoring',   r: r => `~$${r.scoring_cost.toFixed(3)}` },
            { k:'screening_cost', l:'Screening', r: r => `~$${r.screening_cost.toFixed(3)}` },
            { k:'total_cost',     l:'Total',     r: r => <span className="font-semibold">~${r.total_cost.toFixed(3)}</span> },
          ]} />}
          {view === 'by-activity' && <Table rows={data?.by_activity||[]} cols={[
            { k:'service',        l:'Activity',     r: r => SERVICE_LABELS[r.service]||r.service },
            { k:'model',          l:'Model',        r: r => <code className="font-mono text-slate-500 text-[10px]">{r.model}</code> },
            { k:'total_calls',    l:'Calls' },
            { k:'avg_tokens_in',  l:'Avg In',  r: r => `~${(r.avg_tokens_in||0).toLocaleString()}` },
            { k:'avg_tokens_out', l:'Avg Out', r: r => `~${(r.avg_tokens_out||0).toLocaleString()}` },
            { k:'avg_latency_ms', l:'Latency', r: r => `${r.avg_latency_ms}ms` },
            { k:'exact_pct',      l:'Exact %', r: r => r.exact_pct > 0 ? <span className="text-emerald-600 font-semibold">{r.exact_pct}%</span> : <span className="text-slate-400">est</span> },
            { k:'total_cost',     l:'Est. Cost',    r: r => <span className="font-semibold">~${r.total_cost.toFixed(4)}</span> },
          ]} />}
          {view === 'by-member'   && <Table rows={data?.by_member||[]} cols={[
            { k:'name',           l:'Member',       r: r => <div><p className="font-medium text-slate-800">{r.name}</p><p className="text-[10px] text-slate-400">{r.email}</p></div> },
            { k:'li_sources',     l:'LI Sources' },
            { k:'scores_run',     l:'Scores Run' },
            { k:'total_calls',    l:'Total Calls' },
            { k:'cost_per_action',l:'Cost/Action', r: r => `~$${r.cost_per_action.toFixed(4)}` },
            { k:'total_cost',     l:'Total Cost',  r: r => <span className="font-semibold">~${r.total_cost.toFixed(4)}</span> },
          ]} />}
        </>
      )}
    </div>
  );
}
